import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { Calendar, Popover, PopoverContent, PopoverTrigger, ShadCNButton } from '@/components';

interface DateRangeFilterProps {
  value: [string, string];
  onStartDateChange: (date: Date | undefined) => void;
  onEndDateChange: (date: Date | undefined) => void;
}

const parseDate = (s: string) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return undefined;
  const [y, m, d] = s.split('-').map(Number) as [number, number, number];
  return new Date(y, m - 1, d);
};

export const DateRangeFilter = ({ value, onStartDateChange, onEndDateChange }: DateRangeFilterProps) => {
  const [start, end] = value;
  const startDate = parseDate(start);
  const endDate = parseDate(end);

  // ⬇️ handle toggle logic here
  const handleStartSelect = (date: Date | undefined) => {
    if (date && startDate && date.toISOString().split('T')[0] === startDate.toISOString().split('T')[0]) {
      onStartDateChange(undefined); // unset if clicked same date
    } else {
      onStartDateChange(date);
    }
  };

  const handleEndSelect = (date: Date | undefined) => {
    if (date && endDate && date.toISOString().split('T')[0] === endDate.toISOString().split('T')[0]) {
      onEndDateChange(undefined); // unset if clicked same date
    } else {
      onEndDateChange(date);
    }
  };

  return (
    <div className="px-[1px] pb-4">
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-2">
          <div className="w-[calc(50%-4px)]">
            From
            <Popover>
              <PopoverTrigger asChild>
                <ShadCNButton
                  variant="outline"
                  data-empty={!start}
                  className="w-full justify-start text-left font-bold data-[empty=true]:text-muted-foreground"
                >
                  <CalendarIcon />
                  {startDate ? format(startDate, 'd MMM, yyyy') : <span>Start date</span>}
                </ShadCNButton>
              </PopoverTrigger>
              <PopoverContent disablePortal className="z-[60] w-auto p-0">
                <Calendar mode="single" selected={startDate} onSelect={handleStartSelect} captionLayout="dropdown" />
              </PopoverContent>
            </Popover>
          </div>

          <div className="w-[calc(50%-4px)]">
            To
            <Popover>
              <PopoverTrigger asChild>
                <ShadCNButton
                  variant="outline"
                  data-empty={!end}
                  className="w-full justify-start text-left font-bold data-[empty=true]:text-muted-foreground"
                >
                  <CalendarIcon />
                  {endDate ? format(endDate, 'd MMM, yyyy') : <span>End date</span>}
                </ShadCNButton>
              </PopoverTrigger>
              <PopoverContent disablePortal className="w-auto p-0" align="end">
                <Calendar
                  mode="single"
                  selected={endDate}
                  onSelect={handleEndSelect}
                  captionLayout="dropdown"
                  showOutsideDays
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>
    </div>
  );
};
